import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import html2pdf from "html2pdf.js";
import { FaRegTimesCircle, FaDownload, FaArrowLeft } from "react-icons/fa";

// Import new modern components
import BookingDetailsHeader from "../components/BookingDetails/BookingDetailsHeader";
import TripDetailsCard from "../components/BookingDetails/TripDetailsCard";
import RideMapCard from "../components/BookingDetails/RideMapCard";
import PaymentDetailsCard from "../components/BookingDetails/PaymentDetailsCard";
import DriverDetailsCard from "../components/BookingDetails/DriverDetailsCard";

// Import shared components and services
import CancellationModal from "../components/shared/CancellationModal";
import RatingModal from "../components/shared/RatingModal";
import TripDetailsSummary from "../components/TripDetailsSummary";
import refundService from "../services/refundService";
import ratingService from "../services/ratingService";
import socketService from "../utils/socketService";
import { API_URL } from "../config/apiConfig";
import { getTimeFromSources, convertTo12HourFormat } from "../utils/timeUtils";
import { formatDistance, formatTravelTime } from "../utils/objectIdUtils";
import {
  getPaymentMethodDisplay,
  getPaymentStatusDisplay,
  getPaymentMethodBadgeClass,
  getPaymentStatusBadgeClass,
  getPaymentMethodIcon,
  getPaymentStatusIcon,
} from "../utils/paymentDisplayUtils";

// Utility function to format date in DD-MM-YYYY format
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "Not set";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    return "Invalid date";
  }
};

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Get status badge color
  const getStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-700";

    const statusLower = status.toLowerCase();
    if (statusLower === "completed") {
      return "bg-green-100 text-green-700";
    }
    if (statusLower === "cancelled") {
      return "bg-red-100 text-red-700";
    }
    if (statusLower === "pending" || statusLower === "confirmed" || statusLower === "assigned") {
      return "bg-blue-100 text-blue-700";
    }
    if (statusLower === "in_progress" || statusLower === "inprogress") {
      return "bg-yellow-100 text-yellow-700";
    }
    return "bg-gray-100 text-gray-700";
  };

  // Get status label
  const getStatusLabel = (status) => {
    if (!status) return "Unknown";
    const statusLower = status.toLowerCase();
    if (statusLower === "inprogress") return "In Progress";
    if (statusLower === "in_progress") return "In Progress";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Get route summary for mobile header
  const getRouteSummary = () => {
    if (!booking) return "";
    const fromCity = booking.pickupAddressCity || booking.from || "";
    const toCity = booking.dropAddressCity || booking.to || "";
    
    const cleanFrom = typeof fromCity === "string" ? fromCity.split(",")[0].trim() : "";
    const cleanTo = typeof toCity === "string" ? toCity.split(",")[0].trim() : "";
    
    if (booking.serviceType === "rental") {
      return `Local Rental in ${cleanFrom}`;
    }
    
    if (cleanFrom && cleanTo && cleanFrom !== cleanTo) {
      return `${cleanFrom} to ${cleanTo}`;
    }
    
    return cleanFrom || cleanTo || "Trip Details";
  };

  const [isLoading, setIsLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [rawBookingData, setRawBookingData] = useState(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [walletOtpData, setWalletOtpData] = useState(null);

  // Get authentication state
  const { user: userInfo } = useSelector((state) => state.auth);
  const corporateToken = localStorage.getItem("corporateToken");
  const isCorporateSession = !!corporateToken && !(userInfo && userInfo.token);

  // Extract and decode query parameters for fallback data
  const queryParams = new URLSearchParams(window.location.search);

  const getDecodedParam = (param, fallbackParam = null) => {
    let value = queryParams.get(param);
    if (!value && fallbackParam) {
      value = queryParams.get(fallbackParam);
    }
    try {
      return value ? decodeURIComponent(value) : "";
    } catch (e) {
      return value || "";
    }
  };

  const fromParam = getDecodedParam("from");
  const toParam = getDecodedParam("to");
  const pickupAddress = getDecodedParam("pickupAddress");
  const dropAddress = getDecodedParam("dropAddress");
  const cabNameParam = queryParams.get("cabName");
  const amountParam = queryParams.get("amount");
  const dateParam = queryParams.get("date");
  const timeParam = queryParams.get("time");
  const pickupTimeParam = queryParams.get("pickupTime");
  const travelTimeParam = queryParams.get("travelTime");
  const distanceParam = queryParams.get("distance");
  const viaCitiesParam = queryParams.get("via");
  const addOnsCostParam = parseFloat(queryParams.get("addOnsCost")) || 0;

  // Parse selectedAddOns from URL parameters
  let selectedAddOnsParam = [];
  try {
    const addOnsParam = queryParams.get("selectedAddOns");
    if (addOnsParam) {
      selectedAddOnsParam = JSON.parse(decodeURIComponent(addOnsParam));
      if (!Array.isArray(selectedAddOnsParam)) {
        selectedAddOnsParam = [];
      }
    }
  } catch (error) {
    console.error("Error parsing selectedAddOns from URL:", error);
    selectedAddOnsParam = [];
  }

  // Helper function to clean location strings and avoid ObjectIds
  const cleanLocationString = (location) => {
    if (!location || typeof location !== "string") return null;
    if (location.length === 24 && /^[0-9a-fA-F]{24}$/.test(location))
      return null;
    return location.trim() || null;
  };

  // Fetch booking details
  const fetchBookingDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let endpoint = "";
      const config = {};

      // Determine the endpoint based on ID format
      if (id.startsWith("CB")) {
        // Public tracking by bookingId
        endpoint = `${API_URL}/booking/track/${id}`;
      } else if (id.startsWith("local-")) {
        // Local booking (shouldn't happen in production)
        setError("Local bookings are not fully supported");
        setIsLoading(false);
        return;
      } else {
        // Regular booking by MongoDB ID
        if (isCorporateSession) {
          endpoint = `${API_URL}/corporate/bookings/${id}`;
          config.headers = { Authorization: `Bearer ${corporateToken}` };
        } else if (userInfo && userInfo.token) {
          endpoint = `${API_URL}/booking/${id}`;
          config.headers = { Authorization: `Bearer ${userInfo.token}` };
        } else {
          endpoint = `${API_URL}/booking/track/${id}`;
        }
      }

      const response = await axios.get(endpoint, {
        ...config,
        timeout: 30000, // 30 second timeout
      });

      if (response.data.success || response.data.data) {
        const bookingData = response.data.data || response.data;

        // Format the booking for display
        const displayBooking = {
          id: bookingData.bookingId || id,
          date: formatDateDDMMYYYY(bookingData.pickupDate || dateParam),
          time: bookingData.pickupTime || timeParam || "Not specified",
          pickupTime: bookingData.pickupTime || timeParam || "Not specified",
          createdAt: bookingData.createdAt,
          from: bookingData.from || fromParam || "Pickup Location",
          to: bookingData.to || toParam || "Drop-off Location",
          pickupAddressCity: bookingData.from || fromParam || "Pickup Location",
          dropAddressCity: bookingData.to || toParam || "Drop-off Location",
          pickupAddress: bookingData.pickupAddress || pickupAddress,
          dropAddress: bookingData.dropAddress || dropAddress,
          distance: bookingData.distance || distanceParam || 0,
          duration: bookingData.duration || travelTimeParam,
          cabType: bookingData.cabType?.name || cabNameParam || "Standard",
          cabImage: bookingData.cabType?.image || "/car.png",
          baseAmount: bookingData.baseAmount || 0,
          taxAmount: bookingData.taxAmount || 0,
          totalAmount: bookingData.totalAmount || amountParam || 0,
          couponDiscountAmount: bookingData.couponDiscountAmount || 0,
          addOnsCost: bookingData.addOnsCost || addOnsCostParam || 0,
          selectedAddOns: bookingData.selectedAddOns || selectedAddOnsParam || [],
          paymentMethod: bookingData.paymentMethod || "COD",
          paymentStatus: bookingData.paymentStatus || "pending",
          paymentId: bookingData.paymentId,
          status: bookingData.status || "pending",
          serviceType: bookingData.serviceType || "outstation",
          tripType: bookingData.journeyType || "oneWay",
          viaCities: (bookingData.viaCities && bookingData.viaCities.length > 0)
            ? bookingData.viaCities
            : (viaCitiesParam ? (viaCitiesParam.includes("|") ? viaCitiesParam.split("|") : [viaCitiesParam]).map(city => city.trim()).filter(Boolean) : []),
          returnDate: bookingData.returnDate,
          returnTime: bookingData.returnTime,
          startOtp: bookingData.startOtp,
          driverName: "To be assigned",
          canCancel: bookingData.status && ["pending"].includes(bookingData.status.toLowerCase()),
          invoiceDownloaded: Boolean(bookingData.invoiceDownloaded),
          invoiceDownloadedAt: bookingData.invoiceDownloadedAt || null,
        };

        setRawBookingData(bookingData);
        setBooking(displayBooking);

        // Check if there is an active, non-expired Wallet OTP in the booking
        if (bookingData.walletOtp && bookingData.walletOtpExpiry) {
          const expiryDate = new Date(bookingData.walletOtpExpiry);
          if (expiryDate > new Date()) {
            console.log("BookingDetails: Found active wallet OTP in fetched data:", bookingData.walletOtp);
            setWalletOtpData({
              bookingId: bookingData._id,
              bookingNumber: bookingData.bookingId,
              otp: bookingData.walletOtp,
              expiresAt: bookingData.walletOtpExpiry,
              message: `Your wallet payment OTP is: ${bookingData.walletOtp}. Share this with the driver to confirm payment.`
            });
          } else {
            setWalletOtpData(null);
          }
        } else {
          setWalletOtpData(null);
        }

        setIsLoading(false);
      } else {
        setError("Failed to load booking details");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Error fetching booking:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to load booking details";
      console.error("Detailed error:", errorMessage);
      setError(errorMessage);
      setBooking(null);
      setRawBookingData(null);
      setIsLoading(false);
    }
  }, [id, userInfo, corporateToken, isCorporateSession, fromParam, toParam, pickupAddress, dropAddress, cabNameParam, amountParam, dateParam, timeParam, pickupTimeParam, travelTimeParam, distanceParam]);

  // Initial fetch
  useEffect(() => {
    fetchBookingDetails();

    // Socket setup for real-time updates
    const userId = userInfo?._id || userInfo?.id;
    if (userInfo && userId) {
      socketService.connect();
      socketService.joinUserRoom(userId);
      console.log('BookingDetails: Joined user room', userId);

      // Join the ride room to receive ride-specific real-time events (e.g. Wallet OTP)
      socketService.joinRideRoom(id);
      console.log('BookingDetails: Joined ride room', id);

      const unsubBooking = socketService.listenForBookingUpdates((updatedBooking) => {
        if (updatedBooking._id === id || updatedBooking.bookingId === id) {
          toast.info("Booking information has been updated");
          fetchBookingDetails();
        }
      });

      // Listen for driver assignment specifically
      const unsubDriver = socketService.listenForDriverAssignment((assignment) => {
        if (assignment.booking?._id === id || assignment.booking?.bookingId === id) {
          toast.success(`Driver ${assignment.driver?.name} assigned to your ride!`);
          fetchBookingDetails();
        }
      });

      const unsubPayment = socketService.listenForPaymentUpdates((paymentUpdate) => {
        if (paymentUpdate.bookingId === id) {
          toast.success("Refund has been processed for this booking");
          fetchBookingDetails();
        }
      });

      // Listen for wallet payment OTP
      const unsubOtp = socketService.listenForEvent("wallet_payment_otp", (data) => {
        const cleanId = (id || "").toString().trim().toLowerCase();
        const cleanBookingId = (data.bookingId || "").toString().trim().toLowerCase();
        const cleanBookingNumber = (data.bookingNumber || "").toString().trim().toLowerCase();

        if (cleanBookingId === cleanId || cleanBookingNumber === cleanId) {
          console.log("BookingDetails: Received wallet payment OTP via socket", data);
          setWalletOtpData(data);
          toast.info(`Wallet Payment OTP: ${data.otp}. Share this with the driver to confirm payment.`, {
            autoClose: 15000,
          });
        }
      });

      return () => {
        if (unsubBooking) unsubBooking();
        if (unsubDriver) unsubDriver();
        if (unsubPayment) unsubPayment();
        if (unsubOtp) unsubOtp();
      };
    }
  }, [id, userInfo, fetchBookingDetails]);

  // Join ride room by MongoDB ID once booking details are loaded
  useEffect(() => {
    if (rawBookingData?._id) {
      socketService.joinRideRoom(rawBookingData._id);
      console.log('BookingDetails: Joined ride room by MongoDB ID', rawBookingData._id);
    }
  }, [rawBookingData]);

  // Handle invoice download - use backend API (One-time download policy)
  const handleDownloadInvoice = async () => {
    if (!rawBookingData?._id) {
      toast.error("Booking information not available");
      return;
    }

    if (booking?.invoiceDownloaded || rawBookingData?.invoiceDownloaded) {
      toast.error("Invoice has already been downloaded once and cannot be downloaded again.");
      return;
    }

    try {
      const token = userInfo?.token || corporateToken;
      if (!token) {
        toast.error("Please login to download invoice");
        return;
      }

      // Call backend API to get download URL
      const response = await axios.get(
        `${API_URL}/booking/${rawBookingData._id}/invoice/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data?.downloadUrl) {
        let finalUrl = response.data.data.downloadUrl;
        // If user is running frontend locally (localhost) and downloadUrl points to remote backend domain, point to current API origin
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          if (finalUrl.includes('backend.cabzigo.com')) {
            const apiHost = new URL(API_URL).origin;
            finalUrl = finalUrl.replace('https://backend.cabzigo.com', apiHost);
          }
        }
        // Update local state immediately to disable future downloads
        setBooking(prev => prev ? ({ ...prev, invoiceDownloaded: true, invoiceDownloadedAt: new Date() }) : prev);
        setRawBookingData(prev => prev ? ({ ...prev, invoiceDownloaded: true, invoiceDownloadedAt: new Date() }) : prev);

        // Open the PDF URL in a new tab/window for download
        window.open(finalUrl, '_blank');
        toast.success("Invoice download started!");
      } else {
        toast.error("Failed to get invoice download URL");
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      if (error.response?.data?.alreadyDownloaded) {
        setBooking(prev => prev ? ({ ...prev, invoiceDownloaded: true }) : prev);
        setRawBookingData(prev => prev ? ({ ...prev, invoiceDownloaded: true }) : prev);
      }
      toast.error(error.response?.data?.message || "Failed to download invoice");
    }
  };

  // Handle booking cancellation
  const handleCancelBooking = async (cancelData) => {
    try {
      setIsCancelling(true);

      if (!userInfo || !userInfo.token) {
        toast.error("You must be logged in to cancel a booking");
        return;
      }

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const response = await axios.put(
        `${API_URL}/booking/${rawBookingData._id}/cancel`,
        {
          reason: cancelData.reason,
          isRefundEligible: cancelData.isRefundEligible,
        },
        config
      );

      if (response.data.success) {
        toast.success("Booking cancelled successfully");
        fetchBookingDetails();
      } else {
        toast.error(response.data.message || "Failed to cancel booking");
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast.error(error.response?.data?.message || "Failed to cancel booking");
    } finally {
      setIsCancelling(false);
      setShowCancellationModal(false);
    }
  };

  // Handle rating submission
  const handleSubmitRating = async (ratingData) => {
    try {
      setIsSubmittingRating(true);

      // Check for either regular user login or corporate login
      const token = userInfo?.token || corporateToken;
      if (!token) {
        toast.error("You must be logged in to rate");
        return;
      }

      // Get driver ID from rawBookingData
      let driverId = null;
      if (rawBookingData?.driver?._id) {
        driverId = rawBookingData.driver._id;
      } else if (rawBookingData?.vendorDriver?._id) {
        driverId = rawBookingData.vendorDriver._id;
      } else if (typeof rawBookingData?.driver === 'string') {
        driverId = rawBookingData.driver;
      } else if (typeof rawBookingData?.vendorDriver === 'string') {
        driverId = rawBookingData.vendorDriver;
      }

      if (!driverId) {
        toast.error("Driver information not available");
        return;
      }

      const response = await ratingService.submitUserRating(
        rawBookingData._id,
        driverId,
        ratingData.rating,
        ratingData.comment,
        token
      );

      if (response.success) {
        toast.success("Rating submitted successfully!");
        fetchBookingDetails();
      } else {
        toast.error("Failed to submit rating");
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      toast.error(error.message || "Failed to submit rating");
    } finally {
      setIsSubmittingRating(false);
      setShowRatingModal(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium text-gray-800">Loading booking details...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !booking) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-white">
        <div className="text-center max-w-md">
          <FaRegTimesCircle className="text-red-500 text-5xl mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const rideId = rawBookingData?.bookingId || rawBookingData?._id;
  const isInProgress  = rawBookingData?.status === 'inProgress';
  const isAssigned    = rawBookingData?.status === 'assigned';
  const isCancelled   = rawBookingData?.status === 'cancelled';
  const trackingUrl   = rideId ? `/track/${rideId}` : null;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* MOBILE HEADER (MakeMyTrip Style) - FIXED to viewport top */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-md">
        <div className="flex items-center px-4 py-3 gap-2">
          <button 
            type="button"
            onClick={() => navigate("/")} 
            className="text-white hover:text-gray-200 transition-colors p-1 flex-shrink-0"
            aria-label="Go Back"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="font-bold !text-sm leading-tight truncate">
              Booking Details
            </p>
            <p className="text-[11px] text-blue-200 font-semibold mt-0.5 truncate">
              Booking #{booking?.id || "N/A"} {booking?.date ? ` • ${booking.date}` : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Spacer to prevent header overlay on mobile */}
      <div className="md:hidden h-[52px]"></div>

      {/* Blue Header Banner - FIRST (Desktop Only) */}
      <div className="hidden md:block mt-4">
        <div className="max-w-[1180px] mx-auto px-4 lg:px-8">
          <div className="bg-blue-600 text-white py-3 px-6 rounded-md">
            <h1 className="text-xl font-semibold">Booking Details</h1>
          </div>
        </div>
      </div>

      {/* 🔒 WALLET PAYMENT OTP BANNER — visible when OTP is generated */}
      {walletOtpData && (
        <div className="max-w-[1180px] mx-auto px-4 lg:px-8 mt-3">
          <div style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
            borderRadius: 10, padding: '16px 20px',
            boxShadow: '0 4px 18px rgba(59,130,246,0.35)',
            border: '1px solid #3b82f6',
            color: 'white',
          }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  🔒 Wallet Payment Authorization Required
                </h3>
                <p className="text-sm text-blue-200 mt-1">
                  Your driver has requested wallet payment. Share this OTP with the driver to confirm:
                </p>
              </div>
              <div className="flex flex-col items-center">
                <div style={{
                  fontSize: '28px',
                  fontWeight: '900',
                  letterSpacing: '0.2em',
                  color: '#10b981',
                  background: 'rgba(15,23,42,0.6)',
                  padding: '6px 20px',
                  borderRadius: '8px',
                  border: '1px solid rgba(59,130,246,0.3)',
                  textAlign: 'center',
                }}>
                  {walletOtpData.otp}
                </div>
                <span className="text-[10px] text-blue-300 mt-1 font-mono">
                  Expires at: {new Date(walletOtpData.expiresAt).toLocaleTimeString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔴 CANCELLED TRIP BANNER — visible when status is 'cancelled' */}
      {isCancelled && (
        <div className="max-w-[1180px] mx-auto px-4 lg:px-8 mt-3">
          <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: 10, padding: '16px 20px',
            boxShadow: '0 4px 18px rgba(239,68,68,0.35)',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                ⚠️ This booking has been cancelled
              </span>
              {(rawBookingData?.cancellationReason || booking?.cancellationReason) && (
                <span style={{ color: '#fee2e2', fontWeight: 500, fontSize: 14 }}>
                  <strong>Reason for Cancellation:</strong> {rawBookingData?.cancellationReason || booking?.cancellationReason}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🟡 DRIVER APPROACHING BANNER — visible when status is 'assigned' */}
      {isAssigned && trackingUrl && (
        <div className="max-w-[1180px] mx-auto px-4 lg:px-8 mt-3">
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: 10, padding: '12px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 10,
            boxShadow: '0 4px 18px rgba(245,158,11,0.35)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: '#fff', display: 'inline-block',
                animation: 'pulse 1.5s infinite',
              }} />
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                🚗 Your driver is on the way to pick you up!
              </span>
            </div>
            <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
              style={{
                background: '#fff', color: '#d97706',
                padding: '7px 18px', borderRadius: 99,
                fontWeight: 700, fontSize: 13,
                textDecoration: 'none', whiteSpace: 'nowrap',
              }}>
              📍 See Driver Location
            </a>
          </div>
        </div>
      )}

      {/* 🟣 LIVE TRACKING BANNER — visible when ride is inProgress */}
      {isInProgress && trackingUrl && (
        <div className="max-w-[1180px] mx-auto px-4 lg:px-8 mt-3">
          <div style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
            borderRadius: 10, padding: '12px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 10,
            boxShadow: '0 4px 18px rgba(99,102,241,0.35)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: '#10b981', display: 'inline-block',
                animation: 'pulse 1.5s infinite',
              }} />
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                🚗 Your ride is in progress — see your driver's live location
              </span>
            </div>
            <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
              style={{
                background: '#fff', color: '#4f46e5',
                padding: '7px 18px', borderRadius: 99,
                fontWeight: 700, fontSize: 13,
                textDecoration: 'none', whiteSpace: 'nowrap',
              }}>
              📍 Track Live
            </a>
          </div>
        </div>
      )}

      {/* White Header Card (Desktop Only) */}
      <div className="hidden md:block">
        <BookingDetailsHeader
          booking={booking}
          onDownloadInvoice={handleDownloadInvoice}
        />
      </div>

      {/* MOBILE STATUS SUMMARY & ACTION CARD */}
      <div className="md:hidden px-4 pt-1.5">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-5">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-[12px] text-gray-500 font-semibold uppercase tracking-wider">
                {booking?.serviceType === "rental"
                  ? "Local Rental"
                  : booking?.serviceType === "airport"
                  ? "Airport Transfer"
                  : "Outstation Trip"}
              </p>
              <h2 className="text-lg font-bold text-gray-900 mt-0.5">
                Booking #{booking?.id || "N/A"}
              </h2>
            </div>
            <span
              className={`${getStatusColor(
                booking?.status
              )} text-xs font-semibold px-3 py-1 rounded-md`}
            >
              {getStatusLabel(booking?.status)}
            </span>
          </div>

          <div className="text-sm text-gray-600 space-y-1 mb-4">
            <p>
              <span className="font-semibold text-gray-800">Route:</span> {getRouteSummary()}
            </p>
            <p>
              <span className="font-semibold text-gray-800">Date:</span> {booking?.date}
            </p>
            <p>
              <span className="font-semibold text-gray-800">Pickup Time:</span> {booking?.time}
            </p>
            {booking?.cabType && (
              <p>
                <span className="font-semibold text-gray-800">Cab Type:</span> <span className="text-blue-600 font-medium">{booking.cabType}</span>
              </p>
            )}
            <p>
              <span className="font-semibold text-gray-800">Total Fare:</span> <span className="font-bold text-gray-900">₹{booking?.totalAmount}</span>
            </p>
          </div>

          {/* Download Invoice for completed trips */}
          {booking?.status?.toLowerCase() === "completed" && (
            booking?.invoiceDownloaded ? (
              <div
                className="w-full bg-gray-100 border border-gray-300 text-gray-500 py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold cursor-not-allowed select-none"
                title="Invoice has already been downloaded once"
              >
                <FaDownload size={14} className="text-gray-400" />
                <span>Invoice Already Downloaded</span>
              </div>
            ) : (
              <button
                onClick={handleDownloadInvoice}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold transition"
              >
                <FaDownload size={14} />
                Download Invoice
              </button>
            )
          )}
        </div>
      </div>

      {/* Main Content - Responsive Grid Layout */}
      <div className="max-w-[1180px] mx-auto px-4 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr_320px] gap-6 lg:h-[400px]">
          {/* LEFT: Trip Details Card */}
          <div className="w-full lg:w-[320px] lg:h-full">
            <TripDetailsCard
              booking={booking}
              rawBookingData={rawBookingData}
            />
          </div>

          {/* CENTER: Map Card - takes remaining space */}
          <div className="w-full lg:h-full">
            <RideMapCard booking={booking} />
          </div>

          {/* RIGHT: Payment & Driver Details (Stacked - scrollable inside 400px height container) */}
          <div className="flex flex-col-reverse lg:flex-col gap-4 w-full lg:w-[320px] lg:h-full lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-300">
            <PaymentDetailsCard
              booking={booking}
              rawBookingData={rawBookingData}
            />
            <DriverDetailsCard
              booking={booking}
              rawBookingData={rawBookingData}
              onAddReview={() => setShowRatingModal(true)}
              onCancel={() => setShowCancellationModal(true)}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <CancellationModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        onConfirm={handleCancelBooking}
        booking={rawBookingData}
      />

      {rawBookingData?._id && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          onSubmit={handleSubmitRating}
          recipientName={booking?.driverName}
          recipientType="driver"
          bookingId={rawBookingData._id}
        />
      )}
    </div>
  );
};

export default BookingDetails;
